import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, EyeOff, Image as ImageIcon, ExternalLink, ArrowUp, ArrowDown } from 'lucide-react';
import axios from 'axios';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    is_active: true,
    order_index: 0
  });

  const { API } = useContext(AuthContext);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/admin/banners`);
      setBanners(response.data);
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast.error('Erro ao carregar banners');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        title: banner.title,
        description: banner.description || '',
        image_url: banner.image_url,
        link_url: banner.link_url || '',
        is_active: banner.is_active,
        order_index: banner.order_index
      });
    } else {
      setEditingBanner(null);
      setFormData({
        title: '',
        description: '',
        image_url: '',
        link_url: '',
        is_active: true,
        order_index: banners.length
      });
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingBanner(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      link_url: '',
      is_active: true,
      order_index: 0
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    if (!formData.image_url.trim()) {
      toast.error('URL da imagem é obrigatória');
      return;
    }

    try {
      if (editingBanner) {
        await axios.put(`${API}/admin/banners/${editingBanner.id}`, formData);
        toast.success('Banner atualizado com sucesso');
      } else {
        await axios.post(`${API}/admin/banners`, formData);
        toast.success('Banner criado com sucesso');
      }
      
      fetchBanners();
      closeDialog();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('Erro ao salvar banner');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Tem certeza que deseja excluir este banner?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/banners/${bannerId}`);
      toast.success('Banner removido com sucesso');
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('Erro ao remover banner');
    }
  };

  const toggleBannerStatus = async (bannerId) => {
    try {
      await axios.put(`${API}/admin/banners/${bannerId}/toggle-status`);
      toast.success('Status do banner atualizado');
      fetchBanners();
    } catch (error) {
      console.error('Error toggling banner status:', error);
      toast.error('Erro ao atualizar status do banner');
    }
  };

  const updateBannerOrder = async (bannerId, newOrder) => {
    try {
      await axios.put(`${API}/admin/banners/${bannerId}`, { order_index: newOrder });
      fetchBanners();
    } catch (error) {
      console.error('Error updating banner order:', error);
      toast.error('Erro ao atualizar ordem');
    }
  };

  const moveBannerUp = (index) => {
    if (index > 0) {
      const banner = banners[index];
      const prevBanner = banners[index - 1];
      updateBannerOrder(banner.id, prevBanner.order_index);
      updateBannerOrder(prevBanner.id, banner.order_index);
    }
  };

  const moveBannerDown = (index) => {
    if (index < banners.length - 1) {
      const banner = banners[index];
      const nextBanner = banners[index + 1];
      updateBannerOrder(banner.id, nextBanner.order_index);
      updateBannerOrder(nextBanner.id, banner.order_index);
    }
  };

  if (loading) {
    return (
      <Card className="card-feira">
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando banners...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Gestão de Banners</h2>
          <p className="text-gray-600">Gerencie os banners do carrossel da página inicial</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()} className="btn-feira">
              <Plus className="w-4 h-4 mr-2" />
              Novo Banner
            </Button>
          </DialogTrigger>
          
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingBanner ? 'Editar Banner' : 'Novo Banner'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Título do banner"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Descrição do banner (opcional)"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="image_url">URL da Imagem *</Label>
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="/uploads/banner.jpg"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Faça upload da imagem no servidor e cole o caminho aqui
                </p>
              </div>

              <div>
                <Label htmlFor="link_url">URL de Destino (opcional)</Label>
                <Input
                  id="link_url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                  placeholder="https://exemplo.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Link para onde o banner deve redirecionar quando clicado
                </p>
              </div>

              <div>
                <Label htmlFor="order_index">Ordem de Exibição</Label>
                <Input
                  id="order_index"
                  type="number"
                  value={formData.order_index}
                  onChange={(e) => setFormData({...formData, order_index: parseInt(e.target.value) || 0})}
                  min="0"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_active">Banner ativo</Label>
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={closeDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="btn-feira">
                  {editingBanner ? 'Atualizar' : 'Criar'} Banner
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Banners List */}
      {banners.length === 0 ? (
        <Card className="card-feira">
          <CardContent className="p-8 text-center">
            <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Nenhum banner encontrado</h3>
            <p className="text-gray-500 mb-6">Crie seu primeiro banner para o carrossel da página inicial</p>
            <Button onClick={() => openDialog()} className="btn-feira">
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Banner
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {banners.map((banner, index) => (
            <Card key={banner.id} className="card-feira">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {/* Preview da imagem */}
                  <div className="w-32 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {banner.image_url ? (
                      <img
                        src={`${API}${banner.image_url}`}
                        alt={banner.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center" style={{display: banner.image_url ? 'none' : 'flex'}}>
                      <ImageIcon className="w-8 h-8 text-gray-400" />
                    </div>
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-800">{banner.title}</h3>
                      <div className="flex items-center gap-2">
                        <Badge variant={banner.is_active ? "default" : "secondary"}>
                          {banner.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                        <Badge variant="outline">
                          Ordem: {banner.order_index}
                        </Badge>
                      </div>
                    </div>

                    {banner.description && (
                      <p className="text-gray-600 text-sm mb-2">{banner.description}</p>
                    )}

                    {banner.link_url && (
                      <div className="flex items-center gap-1 text-blue-600 text-sm mb-3">
                        <ExternalLink className="w-3 h-3" />
                        <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {banner.link_url}
                        </a>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDialog(banner)}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleBannerStatus(banner.id)}
                      >
                        {banner.is_active ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                        {banner.is_active ? 'Desativar' : 'Ativar'}
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moveBannerUp(index)}
                        disabled={index === 0}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moveBannerDown(index)}
                        disabled={index === banners.length - 1}
                      >
                        <ArrowDown className="w-3 h-3" />
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(banner.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerManagement;